import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { sendEmailVerification, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

import logo from './../../assets/icons/Variant=ARIS Robotics, Colour=Green.svg';
import { auth, functions } from '../../firebase.js';
import { SIGNUP_TARGETS } from '../../common/signupLinkApi.js';
import './../../App.css';
import './../LoginPage/LoginPage.css';
import './SignupPage.css';

const MIN_PASSWORD_LENGTH = 8;

// Every refusal the redeem function can give, by `details.reason`. An unknown token reads
// the same as any other invalid link, so the page never confirms that a token exists.
export const SIGNUP_ERROR_MESSAGES = {
    invalid: 'Linket er ugyldigt.',
    revoked: 'Linket er trukket tilbage. Kontakt ARIS Robotics.',
    company_not_ready: 'Virksomheden er ikke klar endnu. Kontakt ARIS Robotics.',
    not_started: 'Linket er ikke åbnet endnu. Prøv igen senere, eller kontakt ARIS Robotics.',
    expired: 'Linket er udløbet. Kontakt ARIS Robotics.',
    full: 'Der er ikke flere pladser på linket. Kontakt ARIS Robotics.',
    email_in_use: 'Emailen er allerede i brug. Brug en anden email.',
    invalid_email: 'Emailen er ikke gyldig.',
    weak_password: `Adgangskoden skal være på mindst ${MIN_PASSWORD_LENGTH} tegn.`,
    missing_name: 'Angiv dit navn.',
    internal: 'Noget gik galt. Prøv igen, eller kontakt ARIS Robotics.',
};

const redeemSignupLink = httpsCallable(functions, 'redeemSignupLink');

function validate({ name, email, password, repeatedPassword }) {
    if (!name.trim()) return SIGNUP_ERROR_MESSAGES.missing_name;
    if (!email.trim()) return SIGNUP_ERROR_MESSAGES.invalid_email;
    if (password.length < MIN_PASSWORD_LENGTH) return SIGNUP_ERROR_MESSAGES.weak_password;
    if (password !== repeatedPassword) return 'Adgangskoderne er ikke ens.';
    return null;
}

const SignupDone = ({ target }) => {
    const destination = SIGNUP_TARGETS[target] || SIGNUP_TARGETS.insights;

    return (
        <div className="card-body">
            <p>Din bruger er oprettet. Vi har sendt en email, så du kan bekræfte din emailadresse.</p>
            {target === 'app' && (
                <p>Du skal logge ind igen med din email og adgangskode, når appen åbner.</p>
            )}
            <div className="card-actions">
                <a className="aris-button-primary signup-destination" href={destination.url}>
                    Gå til {destination.label}
                </a>
            </div>
        </div>
    );
};

// The page a sign-up link opens. The slug in the URL is only there to read well and is never
// looked at; the token after it is the link's secret.
const SignupPage = () => {
    const { token } = useParams();
    const [user, loadingUser] = useAuthState(auth);
    const [form, setForm] = useState({ name: '', email: '', password: '', repeatedPassword: '' });
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [doneTarget, setDoneTarget] = useState(null);

    const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

    const submit = async (event) => {
        event.preventDefault();
        const validationError = validate(form);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);
        setError(null);
        let target;
        try {
            const result = await redeemSignupLink({
                token,
                name: form.name.trim(),
                email: form.email.trim(),
                password: form.password,
            });
            target = result.data.target;
        } catch (redeemError) {
            const reason = redeemError?.details?.reason;
            setError(SIGNUP_ERROR_MESSAGES[reason] || SIGNUP_ERROR_MESSAGES.internal);
            setSubmitting(false);
            return;
        }

        // The account exists from here on; signing in and the verification email are a
        // convenience, so a failure in either still shows the way onwards.
        setDoneTarget(target);
        try {
            const credentials = await signInWithEmailAndPassword(auth, form.email.trim(), form.password);
            await sendEmailVerification(credentials.user);
        } catch (signInError) {
            console.error('Signed up, but could not sign in or send the verification email', signInError);
        }
    };

    const renderBody = () => {
        if (doneTarget) return <SignupDone target={doneTarget} />;
        if (loadingUser) return null;

        if (user) {
            return (
                <div className="card-body">
                    <p>
                        Du er logget ind som {user.email}. Log ud for at oprette en ny bruger med dette link.
                    </p>
                    <div className="card-actions">
                        <button className="aris-button-primary" type="button" onClick={() => signOut(auth)}>
                            Log ud
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <form className="card-body" onSubmit={submit} noValidate>
                <div className="login_card">
                    <input
                        className="aris-input"
                        aria-label="Navn"
                        placeholder="Navn"
                        autoComplete="name"
                        value={form.name}
                        onChange={update('name')}
                    />
                    <input
                        className="aris-input"
                        type="email"
                        aria-label="Email"
                        placeholder="Email"
                        autoComplete="email"
                        value={form.email}
                        onChange={update('email')}
                    />
                    <input
                        className="aris-input"
                        type="password"
                        aria-label="Adgangskode"
                        placeholder="Adgangskode"
                        autoComplete="new-password"
                        value={form.password}
                        onChange={update('password')}
                    />
                    <input
                        className="aris-input"
                        type="password"
                        aria-label="Gentag adgangskode"
                        placeholder="Gentag adgangskode"
                        autoComplete="new-password"
                        value={form.repeatedPassword}
                        onChange={update('repeatedPassword')}
                    />
                </div>
                <div className="card-actions">
                    <div className="login-error" role="alert">{error}</div>
                    <button className="aris-button-primary" type="submit" disabled={submitting}>
                        Opret bruger
                    </button>
                </div>
            </form>
        );
    };

    return (
        <div className="LoginPage SignupPage">
            <img className="main-logo" src={logo} alt="Aris Robotics" />
            <div className="login-container card-container">
                <div className="card-header">
                    <div className="card-header-title">Opret bruger</div>
                </div>
                {renderBody()}
            </div>
        </div>
    );
};

export default SignupPage;
